import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { CopyScheduleConflictDto } from '../../api/scheduler/models/copy-schedule-conflict-dto';
import { SchedulerMasterScheduleApiService } from '../../features/master-schedule/services/scheduler-master-schedule-api.service';
import { ToastActions } from '../toast/toast.actions';
import { MasterScheduleActions } from './master-schedule.actions';
import { crewAvailabilityFromCalendar } from '../../features/master-schedule/utils/master-schedule.mapper';
import { userFacingApiError } from './master-schedule-user-errors';
import {
  selectDotAvailabilityRange,
  selectMasterScheduleQuery,
  selectMasterScheduleSearch,
} from './master-schedule.selectors';

@Injectable()
export class MasterScheduleEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(SchedulerMasterScheduleApiService);
  private readonly store = inject(Store);

  loadMasterSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterScheduleActions.load),
      switchMap(({ startDate, endDate, search }) =>
        forkJoin({
          calendar: this.api.getCalendar(startDate, endDate),
          legends: this.api.getLegends(),
        }).pipe(
          map(({ calendar, legends }) =>
            MasterScheduleActions.loadSuccess({
              calendar: calendar.data ?? null,
              legends: legends.data ?? null,
              search: search ?? '',
            })
          ),
          catchError((error) => {
            console.error(error);
            const message = userFacingApiError(error);
            return of(
              MasterScheduleActions.loadFailure({ error: message }),
              ToastActions.showToast({
                severity: 'error',
                summary: 'Master schedule',
                detail: message,
              })
            );
          })
        )
      )
    )
  );

  loadRangeAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterScheduleActions.loadRangeAvailability),
      switchMap(({ startDate, endDate }) =>
        this.api.getCalendar(startDate, endDate).pipe(
          map((calendar) =>
            MasterScheduleActions.loadRangeAvailabilitySuccess({
              startDate,
              endDate,
              availability: crewAvailabilityFromCalendar(calendar.data ?? null),
            })
          ),
          catchError((error) => {
            console.error(error);
            return of(MasterScheduleActions.loadRangeAvailabilityFailure());
          })
        )
      )
    )
  );

  /** Writes refresh the grid for whatever window is loaded. Dots stay on the selected range. */
  reloadDotAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterScheduleActions.refresh),
      withLatestFrom(this.store.select(selectDotAvailabilityRange)),
      mergeMap(([, range]) => {
        if (!range) {
          return EMPTY;
        }
        return of(MasterScheduleActions.loadRangeAvailability(range));
      })
    )
  );

  refreshMasterSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterScheduleActions.refresh),
      withLatestFrom(
        this.store.select(selectMasterScheduleQuery),
        this.store.select(selectMasterScheduleSearch)
      ),
      map(([, query, search]) => {
        if (!query) {
          return MasterScheduleActions.loadFailure({
            error: 'No schedule query available to refresh',
          });
        }
        return MasterScheduleActions.load({
          startDate: query.startDate,
          endDate: query.endDate,
          search,
          legendMode: query.legendMode,
        });
      })
    )
  );

  loadReferenceData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterScheduleActions.loadReferenceData),
      switchMap(() =>
        this.api.getReferenceData().pipe(
          map((response) =>
            MasterScheduleActions.loadReferenceDataSuccess({ data: response.data ?? null })
          ),
          catchError((error) => {
            console.error(error);
            const message = userFacingApiError(error);
            return of(
              MasterScheduleActions.loadReferenceDataFailure({ error: message }),
              ToastActions.showToast({
                severity: 'error',
                summary: 'Master schedule',
                detail: message,
              })
            );
          })
        )
      )
    )
  );

  loadAvailableCrew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterScheduleActions.loadAvailableCrew),
      switchMap(({ dateFrom, dateTo, rotation, rank }) =>
        this.api
          .listAvailableCrew({ dateFrom, dateTo, rotation, rank, assignmentStatus: 'UNASSIGNED' })
          .pipe(
            map((response) =>
              MasterScheduleActions.loadAvailableCrewSuccess({
                items: response.data ?? [],
                summaryMessage: response.summaryMessage ?? '',
              })
            ),
            catchError((error) => {
              console.error(error);
              const message = userFacingApiError(error);
              return of(
                MasterScheduleActions.loadAvailableCrewFailure({ error: message }),
                ToastActions.showToast({
                  severity: 'error',
                  summary: 'Master schedule',
                  detail: message,
                })
              );
            })
          )
      )
    )
  );

  assign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterScheduleActions.assign),
      mergeMap(({ body }) =>
        this.api.assignAllocations(body).pipe(
          mergeMap(() => of(...writeSuccessActions('Crew assigned to the schedule', 'assign'))),
          catchError((error) => of(...writeFailureActions(error, 'assign')))
        )
      )
    )
  );

  replace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterScheduleActions.replace),
      mergeMap(({ body }) =>
        this.api.assignAllocations(body).pipe(
          mergeMap(() => of(...writeSuccessActions('Allocation updated'))),
          catchError((error) => of(...writeFailureActions(error, 'replace')))
        )
      )
    )
  );

  loadAllocationDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterScheduleActions.loadAllocationDetail),
      switchMap(({ id }) =>
        this.api.getAllocation(id).pipe(
          map((response) =>
            MasterScheduleActions.loadAllocationDetailSuccess({
              allocation: response.data?.allocation ?? null,
            })
          ),
          catchError((error) => {
            console.error(error);
            return of(
              MasterScheduleActions.loadAllocationDetailFailure({
                error: userFacingApiError(error),
              })
            );
          })
        )
      )
    )
  );

  deleteSelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterScheduleActions.deleteSelected),
      mergeMap(({ body }) =>
        this.api.deleteSelectedAllocations(body).pipe(
          mergeMap(() => of(...writeSuccessActions('Allocation deleted'))),
          catchError((error) => of(...genericWriteFailure(error)))
        )
      )
    )
  );

  copy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterScheduleActions.copy),
      mergeMap(({ body }) =>
        this.api.copySchedule(body).pipe(
          mergeMap(() => of(...writeSuccessActions('Schedule copied', 'copy'))),
          catchError((error) => of(...writeFailureActions(error, 'copy')))
        )
      )
    )
  );
}

function writeSuccessActions(detail: string, kind?: 'assign' | 'copy') {
  const completed =
    kind === 'assign'
      ? [MasterScheduleActions.assignSuccess()]
      : kind === 'copy'
        ? [MasterScheduleActions.copySuccess()]
        : [];
  return [
    ...completed,
    MasterScheduleActions.refresh(),
    ToastActions.showToast({
      severity: 'success',
      summary: 'Master schedule',
      detail,
    }),
  ];
}

function writeFailureActions(error: unknown, kind: 'assign' | 'replace' | 'copy') {
  console.error(error);
  const message = userFacingApiError(error, kind);
  const toast = ToastActions.showToast({
    severity: 'error',
    summary: 'Master schedule',
    detail: message,
  });
  if (kind === 'copy') {
    const conflicts = copyConflicts(error);
    const failure = MasterScheduleActions.copyFailure({ error: message, conflicts });
    // Overwrite conflicts are explained inline next to the Overwrite button; skip the toast.
    return conflicts.length ? [failure] : [failure, toast];
  }
  if (kind === 'replace') {
    return [MasterScheduleActions.replaceFailure({ error: message }), toast];
  }
  return [MasterScheduleActions.assignFailure({ error: message }), toast];
}

function genericWriteFailure(error: unknown) {
  console.error(error);
  const message = userFacingApiError(error);
  return [
    MasterScheduleActions.loadFailure({ error: message }),
    ToastActions.showToast({
      severity: 'error',
      summary: 'Master schedule',
      detail: message,
    }),
  ];
}

function copyConflicts(error: unknown): CopyScheduleConflictDto[] {
  if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
    return [];
  }
  const body = error.error as { conflicts?: CopyScheduleConflictDto[] } | undefined;
  return body?.conflicts ?? [];
}
